// Console data layer: a small stale-while-revalidate cache over api(), the console context (who
// is signed in, which site, what they may do), and helpers every screen uses.
import { createContext, useCallback, useContext, useEffect, useReducer, useState, useSyncExternalStore } from "react";
import { can as canDo, type Membership, type Permission } from "@vavr/core";
import { useToast } from "@vavr/ui";
import { api, ApiError, blobUrl, navigate } from "../shared/index.ts";

export interface SiteSummary { id: string; org_id: string; name: string; state: string; tz: string; kind: string; /** "operator": a bus company's depot. */ org_kind?: string }
export interface Me {
  account: { id: string; email: string; name: string; totp: boolean; phone?: string | null };
  session: { mfa: boolean; client: string };
  /** Vavr support looking read-only, with consent (PMU-290). */
  view?: { grant: string; by: string | null; expires_at: number } | null;
  memberships: (Membership & { adult_id: string | null })[];
  sites: SiteSummary[];
  /** A Vavr operator, who may open the platform console. */
  platform?: boolean;
  /** The modules each of the caller's organisations has (PMU-185). */
  modules?: Record<string, string[]>;
}

type Entry = { data?: unknown; error?: Error; loading: boolean; at: number };

/**
 * GET responses keyed by path. Screens show what is cached at once and refresh in the
 * background, so moving between screens is instant. Tests pass a prefilled cache.
 */
export class ApiCache {
  private entries = new Map<string, Entry>();
  private listeners = new Set<() => void>();
  private inflight = new Map<string, Promise<unknown>>();
  private resolve?: (path: string) => unknown;
  /** `initial` and `resolve` prefill responses (tests, server rendering); a prefilled entry never refetches. */
  constructor(initial: Record<string, unknown> = {}, resolve?: (path: string) => unknown) {
    for (const [k, v] of Object.entries(initial)) this.entries.set(k, { data: v, loading: false, at: Number.MAX_SAFE_INTEGER });
    this.resolve = resolve;
  }
  subscribe = (l: () => void) => { this.listeners.add(l); return () => { this.listeners.delete(l); }; };
  get(path: string): Entry | undefined {
    const e = this.entries.get(path);
    if (e || !this.resolve) return e;
    const data = this.resolve(path);
    if (data === undefined) return undefined;
    const made = { data, loading: false, at: Number.MAX_SAFE_INTEGER };
    this.entries.set(path, made);
    return made;
  }
  private set(path: string, e: Entry) { this.entries.set(path, e); this.listeners.forEach((l) => l()); }
  fetch(path: string, maxAgeMs = 0): Promise<unknown> {
    const cur = this.get(path);
    if (cur && !cur.error && !cur.loading && Date.now() - cur.at < maxAgeMs) return Promise.resolve(cur.data);
    const running = this.inflight.get(path);
    if (running) return running;
    this.set(path, { ...(cur ?? { at: 0 }), loading: true });
    const p = api("GET", path).then(
      (data) => { this.set(path, { data, loading: false, at: Date.now() }); return data; },
      (error: Error) => { handleAuthError(error); this.set(path, { ...(cur ?? {}), error, loading: false, at: Date.now() }); throw error; },
    ).finally(() => this.inflight.delete(path));
    this.inflight.set(path, p);
    return p;
  }
  /** Refetches, in the background, every cached path that starts with `prefix`. */
  invalidate(prefix: string) {
    for (const k of [...this.entries.keys()]) if (k.startsWith(prefix)) this.fetch(k).catch(() => undefined);
  }
}

export const CacheContext = createContext<ApiCache>(new ApiCache());

/** Admin roles without a second factor are sent to set one up (the API says mfa_required). */
export function handleAuthError(e: unknown) {
  if (e instanceof ApiError && e.code === "mfa_required" && typeof location !== "undefined") navigate("/account/security?setup=1", true);
}

export interface Loaded<T> { data: T | null; error: Error | null; loading: boolean; reload: () => void }

/** Reads `path` through the cache; null skips. Refetches on mount unless fresher than `maxAgeMs`. */
export function useGet<T = any>(path: string | null, maxAgeMs = 0): Loaded<T> {
  const cache = useContext(CacheContext);
  const snapshot = useSyncExternalStore(cache.subscribe, () => (path ? cache.get(path) : undefined), () => (path ? cache.get(path) : undefined));
  useEffect(() => { if (path) cache.fetch(path, maxAgeMs).catch(() => undefined); }, [cache, path, maxAgeMs]);
  const reload = useCallback(() => { if (path) cache.fetch(path).catch(() => undefined); }, [cache, path]);
  return { data: (snapshot?.data as T) ?? null, error: snapshot?.error ?? null, loading: path ? !snapshot || (snapshot.loading && snapshot.data === undefined) : false, reload };
}

// ---------- console context ----------

export interface ConsoleCtx {
  me: Me;
  site: SiteSummary;
  base: string; // "/api/v1/sites/<id>"
  can: (perm: Permission) => boolean;
  setSite: (id: string) => void;
}

export const ConsoleContext = createContext<ConsoleCtx | null>(null);
/** Several cached GETs at once (global search): fetched when the list changes, re-rendered as each arrives. */
export function useGetMany(paths: string[], maxAgeMs = 0): (Entry | undefined)[] {
  const cache = useContext(CacheContext);
  const [, bump] = useReducer((x: number) => x + 1, 0);
  useEffect(() => cache.subscribe(bump), [cache]);
  const key = paths.join("\n");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { for (const p of paths) cache.fetch(p, maxAgeMs).catch(() => undefined); }, [cache, key, maxAgeMs]);
  return paths.map((p) => cache.get(p));
}

export function useConsole(): ConsoleCtx {
  const c = useContext(ConsoleContext);
  if (!c) throw new Error("useConsole outside the console");
  return c;
}

export const makeCan = (me: Me, site: SiteSummary) => (perm: Permission) => canDo(me.memberships, perm, { orgId: site.org_id, siteId: site.id });

// ---------- actions ----------

/** Runs a mutation with a busy flag, a toast on success, the error shown, and cache invalidation. */
export function useAction() {
  const toast = useToast();
  const cache = useContext(CacheContext);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async <T,>(fn: () => Promise<T>, o: { success?: string; invalidate?: string[] } = {}): Promise<T | undefined> => {
    setBusy(true); setError(null);
    try {
      const r = await fn();
      for (const p of o.invalidate ?? []) cache.invalidate(p);
      if (o.success) toast.show({ title: o.success, tone: "success" });
      return r;
    } catch (e) {
      handleAuthError(e);
      setError(e instanceof ApiError ? e.message : "That did not work. Try again.");
      return undefined;
    } finally {
      setBusy(false);
    }
  }, [cache, toast]);
  return { run, busy, error, setError };
}

/** Refetches every cached path under `prefix` (after a live update). */
export function useRefresh() {
  const cache = useContext(CacheContext);
  return useCallback((...prefixes: string[]) => { for (const p of prefixes) cache.invalidate(p); }, [cache]);
}

// ---------- blobs ----------

/** Object URL for an authorised blob (photo, signature, document), revoked on unmount. */
export function useBlob(base: string, sha: string | null | undefined): string | undefined {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!sha) { setUrl(undefined); return; }
    let live = true; let made: string | undefined;
    blobUrl(`${base}/blobs/${sha}`).then((u) => { made = u; if (live) setUrl(u); else URL.revokeObjectURL(u); }, () => live && setUrl(undefined));
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [base, sha]);
  return url;
}

/** Saves text (CSV, JSON) as a file in the browser. */
export function download(filename: string, text: string, type = "text/csv;charset=utf-8") {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.rel = "noopener";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------- storage (per-viewer convenience only) ----------

export const store = {
  get(k: string): string | null { try { return typeof localStorage === "undefined" ? null : localStorage.getItem(k); } catch { return null; } },
  set(k: string, v: string) { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
};
